import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { createTeamSchema, CreateTeamFormValues } from '../../lib/validations/teamSchema';
import { useTeams } from '../../hooks/useTeams';
import Button from '../common/Button';
import Input from '../common/Input';
import Card from '../common/Card';
import Alert from '../common/Alert';

export const CreateTeamForm = () => {
  const navigate = useNavigate();
  const { createTeam } = useTeams();
  const [error, setError] = useState<string | null>(null);
  
  const { register, handleSubmit, formState: { errors } } = useForm<CreateTeamFormValues>({
    resolver: zodResolver(createTeamSchema),
    defaultValues: {
      name: '',
      shifts_per_provider: 13,
      day_providers_required: 2,
      night_providers_required: 1,
      scheduling_period_days: 28,
      max_consecutive_shifts: 5,
      min_rest_hours: 10
    }
  });
  
  const onSubmit = async (data: CreateTeamFormValues) => {
    setError(null);
    
    try {
      const result = await createTeam.mutateAsync(data);
      navigate(`/teams/${result.id}/setup`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team. Please try again.');
    }
  };
  
  return (
    <Card className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-6">Create a New Team</h2>
      
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <Input
            label="Team Name"
            {...register('name')}
            error={errors.name?.message}
            placeholder="Emergency Department"
            required
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="w-full">
            <Input
              type="number"
              label="Shifts Per Provider"
              {...register('shifts_per_provider', { valueAsNumber: true })}
              error={errors.shifts_per_provider?.message}
              required
              fullWidth
            />
          </div>
          
          <div className="w-full">
            <Input
              type="number"
              label="Scheduling Period (Days)"
              {...register('scheduling_period_days', { valueAsNumber: true })}
              error={errors.scheduling_period_days?.message}
              required
              fullWidth
            />
          </div>
          
          <div className="w-full">
            <Input
              type="number"
              label="Day Providers Required"
              {...register('day_providers_required', { valueAsNumber: true })}
              error={errors.day_providers_required?.message}
              required
              fullWidth
            />
          </div>
          
          <div className="w-full">
            <Input
              type="number"
              label="Night Providers Required"
              {...register('night_providers_required', { valueAsNumber: true })}
              error={errors.night_providers_required?.message}
              required
              fullWidth
            />
          </div>
          
          <div className="w-full">
            <Input
              type="number"
              label="Max Consecutive Shifts"
              {...register('max_consecutive_shifts', { valueAsNumber: true })}
              error={errors.max_consecutive_shifts?.message}
              required
              fullWidth
            />
          </div>
          
          <div className="w-full">
            <Input
              type="number"
              label="Minimum Rest Hours"
              {...register('min_rest_hours', { valueAsNumber: true })}
              error={errors.min_rest_hours?.message}
              required
              fullWidth
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={createTeam.isPending}
          >
            Create Team
          </Button>
        </div>
      </form>
    </Card>
  );
}; 